import React, { lazy, Suspense, useState, useEffect } from 'react';
import './Hero.css';

// Lazy load the 3D Canvas to reduce initial bundle size
const Hero3DCanvas = lazy(() => import('./Hero3DCanvas'));

const Hero = () => {
  const [load3D, setLoad3D] = useState(false);
  
  useEffect(() => {
    // NO cargar 3D en móviles para mejor performance
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      // En móviles, NO cargar el canvas 3D
      setLoad3D(false);
      return;
    }
    
    // En desktop, cargar después de 500ms
    const timer = setTimeout(() => {
      setLoad3D(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const scrollToServices = () => {
    document.getElementById('servicios')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen md:h-screen flex items-center justify-center overflow-hidden pt-16 sm:pt-20">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/20 via-transparent to-emerald-900/20 z-10"></div>
      
      {/* Three.js Background - Lazy loaded */}
      <div className="absolute inset-0 z-0">
        {load3D ? (
          <Suspense fallback={
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 animate-pulse" />
          }>
            <Hero3DCanvas />
          </Suspense>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
            {/* Mobile animated gradient orbs */}
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-indigo-500/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-500/25 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          </div>
        )}
      </div>
      
      {/* Interactive floating elements */}
      <div className="floating-dot floating-dot-1" />
      <div className="floating-dot floating-dot-2" />
      <div className="floating-dot floating-dot-3" />

      {/* Content */}
      <div className="z-20 text-center px-2 sm:px-4 py-4 sm:py-8 hero-content">
        <div className="max-w-5xl mx-auto">
          {/* Título con logo integrado en lugar de la V */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold mb-8 sm:mb-12 md:mb-16 text-slate-100 leading-[1.2] sm:leading-[1.3] md:leading-[1.5] px-2 sm:px-4 hero-title flex items-center justify-center gap-0.5">
            {/* Logo como la V */}
            <img 
              src="/images/logoVaporDesignsSolutions-removebg-preview.png" 
              alt="V"
              className="w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 xl:w-36 xl:h-36 inline-block rounded-[10%] shadow-lg"
              style={{ 
                filter: 'drop-shadow(0 0 25px rgba(139, 58, 156, 0.6))'
              }}
            />
            <span className="[background:linear-gradient(to_right,rgb(129,140,248),rgb(192,132,252),rgb(52,211,153))] bg-clip-text [-webkit-text-fill-color:transparent]">
              aporDesigns
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl max-w-3xl mx-auto text-slate-100 mb-3 sm:mb-4 leading-relaxed px-2 sm:px-4 hero-subtitle">
            Soluciones digitales a medida para impulsar tu negocio
          </p>
          
          <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto text-slate-200 mb-6 sm:mb-8 px-2 sm:px-4 hero-description">
            Transformamos ideas en experiencias digitales extraordinarias
          </p>
          
          {/* CTAs principales */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12 px-2 sm:px-0 hero-ctas">
            <button 
              type="button"
              onClick={scrollToServices}
              className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-tr from-indigo-500 to-emerald-500 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white shadow-lg shadow-indigo-500/20 transition-all w-full sm:w-auto hover:scale-105 active:scale-95"
            >
              Comenzar proyecto
              <svg 
                className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            
            <button 
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-emerald-400/30 bg-emerald-500/10 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-emerald-300 backdrop-blur-sm transition-all w-full sm:w-auto hover:scale-105 hover:bg-emerald-500/20 hover:border-emerald-400/50 active:scale-95"
            >
              Ver precios
              <svg 
                className="w-4 h-4 sm:w-5 sm:h-5 transition-transform hover:translate-y-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </button>
          </div>

        </div>
      </div>

    </section>
  );
};

export default Hero;
